// src/pages/HomePage/HomePage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation/Navigation';
import Footer from '../../components/Footer/Footer';
import ZakatCalculatorCard from '../../components/ZakatCalculatorCard/ZakatCalculatorCard';
import { siteConfig, getAvailableCategories } from '../../config/siteConfig';
import './HomePage.css';
import SEO from '../../components/SEO/SEO';
import DateDisplay from '../../components/DateDisplay/DateDisplay';
import PrayerTimesCompact from '../../components/PrayerTimesCompact/PrayerTimesCompact';

// Déclaration du type pour l'événement beforeinstallprompt
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const HomePage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      console.log('💡 Application installable détectée');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Écouter l'événement d'installation
    const handleAppInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
      console.log('✅ Application installée avec succès');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('⚠️ Aucun prompt d\'installation disponible');
      return;
    }

    // Afficher le prompt d'installation
    deferredPrompt.prompt();

    // Attendre la réponse de l'utilisateur
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('✅ Utilisateur a accepté l\'installation');
    } else {
      console.log('❌ Utilisateur a refusé l\'installation');
    }

    // Réinitialiser le prompt
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const categories = getAvailableCategories();

  return (
    <div className={`home-page ${isVisible ? 'visible' : ''}`}>
      <Navigation transparent />
      
      <SEO 
        title="Accueil - Mâhî Bâyân | Paroles de Savants"
        description="Traductions authentiques des paroles de savants. Articles islamiques, invocations, fatwas, ruqya et enseignements de l'Université Islamique de Médine par un diplômé."
        url="https://mahi-bayan.netlify.app/"
      />
      
      {/* Hero Section avec bannière */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="/images/entree-masjid.jpg" 
            alt="Bannière Paroles de Savants" 
            className="hero-banner"
          />
          <div className="hero-overlay"></div>
        </div>

        <DateDisplay />
        
        <div className="hero-content">
          <p className="hero-description hero-description-main">
            <span className="description-line-1">Diplômé de l'Université Islamique de Médine,</span>
            <span className="description-line-2">je partage ici des enseignements précieux</span>
            <span className="description-line-3">In Sha Allah</span>
          </p>
          
          <div className="hero-actions">
            <div className="hero-logos-container">
              {/* Logo Zakat */}
              <div className="zakat-logo-button-container">
                <a 
                  href="https://zakat-calculateur.netlify.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="zakat-logo-button"
                  title="Accéder au Calculateur de Zakat"
                >
                  <img 
                    src="/images/logoZakat.png" 
                    alt="Calculateur de Zakat" 
                    className="zakat-hero-logo"
                  />
                  <div className="zakat-logo-tooltip">
                    <span className="tooltip-text">Calculateur de Zakat</span>
                    <span className="tooltip-arrow">↗</span>
                  </div>
                </a>
              </div>
              
              {/* Logo P&F */}
              <div className="floating-pf-logo">
                <a href="#about" className="pf-logo-link">
                  <img 
                    src="/images/logoP&F.jpg" 
                    alt="Mâhî Bâyân" 
                    className="pf-floating-logo"
                  />
                </a>
              </div>

              {/* Bouton d'installation PWA */}
              {isInstallable && (
                <div className="pwa-install-container">
                  <button 
                    onClick={handleInstallClick}
                    className="pwa-install-button"
                    title="Installer l'application"
                  >
                    <div className="pwa-install-icon">
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L12 16M12 16L8 12M12 16L16 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3 17V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="pwa-install-tooltip">
                      <span className="tooltip-text">Installer l'app</span>
                      <span className="tooltip-arrow">↗</span>
                    </div>
                  </button>
                  
                  <div className="pwa-pointer-arrow">
                    <svg width="65" height="65" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 5L9 15" stroke="white" strokeWidth="3.5" strokeLinecap="round"/>
                      <path d="M9 15L9 8" stroke="white" strokeWidth="3.5" strokeLinecap="round"/>
                      <path d="M9 15L16 15" stroke="white" strokeWidth="3.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ✅ Horaires de prières compact - AU MÊME NIVEAU que DateDisplay */}
        <PrayerTimesCompact />
        
        <div className="hero-scroll-indicator">
          <div className="scroll-mouse">
            <div className="scroll-wheel"></div>
          </div>
        </div>
      </section>

      {/* Section Catégories */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              <span className="title-french">Catégories</span>
            </h2>
            <p className="section-description">
              Explorez nos différentes sections
            </p>
          </div>
          
          <div className="categories-grid">
            {categories.map((category) => (
              <div 
                key={category.id} 
                className={`category-card ${category.status === 'soon' ? 'coming-soon' : ''}`}
              >
                <div className="category-header">
                  <div className="category-icon">{category.icon}</div>
                  <h3 className="category-title">{category.title}</h3>
                  {category.status === 'soon' && (
                    <span className="category-badge">Bientôt</span>
                  )}
                </div>
                
                <p className="category-description">{category.description}</p>
                
                {category.subcategories.length > 0 && (
                  <div className="subcategories">
                    {category.subcategories.map((sub) => (
                      <Link 
                        key={sub.id}
                        to={sub.path || '#'}
                        className={`subcategory-item ${sub.status === 'soon' ? 'disabled' : ''}`}
                      >
                        <span className="sub-title">{sub.title}</span>
                        <div className="sub-meta">
                          {sub.count && <span className="sub-count">{sub.count} éléments</span>}
                          {sub.author && <span className="sub-author">{sub.author}</span>}
                          {sub.status === 'soon' && <span className="sub-badge">Bientôt</span>}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section À propos */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="title-french">Me concernant</span>
                </h2>
              </div>
              
              <div className="about-bio">
                <p>
                  Assalâmou 'alaykoum wa rahmatoullâhi wa barakâtouh,
                </p>
                <p>
                  À travers ce site, je partage des traductions aussi fidèles et authentiques que possible des enseignements de nos savants, dans le but de rendre ce savoir précieux accessible à la communauté, à mon humble échelle.
                </p>
                <p>
                  Qu'Allâh nous donne la sincérité et accepte nos oeuvres...
                </p>
              </div>
              
              <div className="credentials-list">
                <h3 className="credentials-title">Parcours académique</h3>
                <ul className="credentials-items">
                  {siteConfig.author.credentials.map((credential, index) => (
                    <li key={index}>{credential}</li>
                  ))}
                </ul>
              </div>
              
              <div className="social-links">
                {siteConfig.author.social.twitter && (
                  <a href={siteConfig.author.social.twitter} target="_blank" rel="noopener noreferrer" className="social-link twitter">
                    <span className="social-icon">𝕏</span>
                    <span className="social-text">Suivez-moi sur X</span>
                  </a>
                )}
                {siteConfig.author.social.youtube && (
                  <a href="https://www.youtube.com/channel/UCqGKAj8SQkTGQztS-h_MIjw" target="_blank" rel="noopener noreferrer" className="social-link youtube">
                    <span className="social-icon">▶</span>
                    <span className="social-text">Chaîne YouTube</span>
                  </a>
                )}
              </div>
            </div>
            
            <div className="about-image">
              <div className="image-container">
                <img 
                  src="/images/parasol-minaret-2.jpg" 
                  alt="Mâhî Bâyân" 
                  className="about-logo"
                />
                <div className="image-decoration"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Zakat Calculateur - Composant isolé */}
      <ZakatCalculatorCard />

      <Footer texts={[]} />

    </div>
  );
};

export default HomePage;
